import * as React from "react";

import { cn } from "lib/utils";

import { AnimatedDialog } from "./animated-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./dialog";

type AppDialogSize = "sm" | "md" | "lg" | "xl";

const SIZE_CLASS: Record<AppDialogSize, string> = {
  sm: "max-w-lg",
  md: "max-w-2xl",
  lg: "max-w-3xl",
  xl: "max-w-4xl",
};

interface AppDialogProps extends Omit<React.ComponentPropsWithoutRef<typeof DialogContent>, "children" | "title"> {
  onClose: () => void;
  title?: React.ReactNode;
  titleClassName?: string;
  headerClassName?: string;
  description?: React.ReactNode;
  descriptionClassName?: string;
  size?: AppDialogSize;
  animated?: boolean;
  isBusy?: boolean;
  closeOnEsc?: boolean;
  closeOnOutside?: boolean;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  bodyClassName?: string;
  actionsClassName?: string;
}

export function AppDialog({
  onClose,
  title,
  titleClassName,
  headerClassName,
  description,
  descriptionClassName,
  size = "md",
  animated = true,
  isBusy = false,
  closeOnEsc = true,
  closeOnOutside = true,
  className,
  bodyClassName,
  actions,
  actionsClassName,
  children,
  ...props
}: AppDialogProps) {
  const resolvedCloseOnEsc = isBusy ? false : closeOnEsc;
  const resolvedCloseOnOutside = isBusy ? false : closeOnOutside;
  const missingDescriptionProps = description ? {} : { "aria-describedby": undefined };
  const hasBody = children !== null && children !== undefined && children !== false;

  const content = (
    <>
      {(title || description) ? (
        <DialogHeader className={headerClassName} data-app-dialog-header data-testid="app-dialog-header">
          {title ? <DialogTitle className={titleClassName}>{title}</DialogTitle> : null}
          {description ? (
            <DialogDescription className={descriptionClassName}>
              {description}
            </DialogDescription>
          ) : null}
        </DialogHeader>
      ) : null}
      {hasBody ? (
        <div className={cn(bodyClassName)} data-app-dialog-body data-testid="app-dialog-body">{children}</div>
      ) : null}
      {actions ? <DialogFooter className={actionsClassName} data-app-dialog-footer data-testid="app-dialog-footer">{actions}</DialogFooter> : null}
    </>
  );

  const sharedClassName = cn(SIZE_CLASS[size], className);

  if (animated) {
    return (
      <AnimatedDialog
        onClose={onClose}
        className={sharedClassName}
        closeOnEsc={resolvedCloseOnEsc}
        closeOnOutside={resolvedCloseOnOutside}
        closeDisabled={isBusy}
        {...missingDescriptionProps}
        {...props}
      >
        {content}
      </AnimatedDialog>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        className={sharedClassName}
        closeDisabled={isBusy}
        onEscapeKeyDown={(event) => {
          if (!resolvedCloseOnEsc) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (!resolvedCloseOnOutside) {
            event.preventDefault();
          }
        }}
        {...missingDescriptionProps}
        {...props}
      >
        {content}
      </DialogContent>
    </Dialog>
  );
}
